import groq from 'groq';
import { BUSINESS_AUDIENCE_FILTER, WWW_AUDIENCE_FILTER } from '@pkg/config/sanity/audience';
import { modularContentBlocks } from '@pkg/modular-blocks/frontend/web-app';
import { slugWithParents } from '@pkg/sanity-utils/slugWithParents';
import type { PAGE_VISIBILITY } from '@pkg/sanity-utils/constants/pageVisibility';
import type { ModularBlock } from '@pkg/sanity-utils/types/modularContentBlocks';
import type { PageSeo } from '@pkg/sanity-utils/types/seo';

export interface BusinessPageTranslation {
  title?: string;
  slug?: string;
  language?: string;
}

export interface BusinessPageContent {
  _type?: string;
  title?: string;
  content?: ModularBlock[];
  pageVisibility?: PAGE_VISIBILITY;
  seoSocial?: PageSeo;
  _translations?: Array<BusinessPageTranslation | null>;
}

export type BusinessPageByIdResult = {
  page: BusinessPageContent | null;
};

/** Business page projection (homepage + 404) with shared modular GROQ. */
export const businessPageFields = groq`
  _id,
  _type,
  title,
  language,
  "slug": slug.current,
  pageVisibility,
  seoSocial {
    metaTitle,
    metaDescription,
    socialImage,
  },
  content[] {
    ${modularContentBlocks}
  }
`;

/**
 * Locale alternates for a translated page. Same source www uses for hreflang.
 * Hidden pages are left out. The parentheses make the filter apply to the dereferenced list,
 * not to each element.
 */
export const businessTranslationFields = groq`
  "_translations": (*[_type == "translation.metadata" && references(^._id)].translations[].value->)[pageVisibility != "hidden"]{
    title,
    ${slugWithParents},
    language
  }
`;

/**
 * Homepage alternates. Prefer the document-internationalization group, same as www.
 * Business homepages are one singleton per locale and are often not linked yet, so fall
 * back to the other published homepages. An empty translation group stays empty.
 * Hidden homepages are left out.
 */
export const businessHomepageTranslationFields = groq`
  "_translations": coalesce(
    (*[_type == "translation.metadata" && references(^._id)][0].translations[].value->)[pageVisibility != "hidden"]{
      title,
      "slug": "/",
      language
    },
    *[_type == "businessHomepage" && slug.current == "/" && !(_id in path("drafts.**")) && pageVisibility != "hidden"]{
      title,
      "slug": "/",
      language
    }
  )
`;

export const businessPageById = groq`{
  "page": *[_id == $id && ${BUSINESS_AUDIENCE_FILTER}][0] {
    ${businessPageFields}
  }
}`;

/**
 * Soft-404 content for a locale: the business-audience 404 page, or, when that locale has none
 * with content, the www 404 page.
 *
 * "Www" here is the www site's audience ({@link WWW_AUDIENCE_FILTER}): `audience-www`, `common`,
 * or untagged. The published page-404 documents are untagged, so a strict `audience-www` match
 * would find nothing.
 *
 * An empty business 404 does not count. `coalesce` returns the first document that exists, so a
 * published-but-empty business page would hide the www fallback and the page would render the
 * "publish the 404 singleton" placeholder.
 *
 * `$id` / `$fallbackId` only break ties when a locale has more than one matching 404 page.
 */
export const businessSoft404Page = groq`{
  "page": coalesce(
    *[
      language == $locale
      && slug.current == "404"
      && ${BUSINESS_AUDIENCE_FILTER}
      && count(content) > 0
    ] | order(select(_id == $id => 0, 1) asc)[0] {
      ${businessPageFields}
    },
    *[
      language == $locale
      && slug.current == "404"
      && ${WWW_AUDIENCE_FILTER}
      && count(content) > 0
    ] | order(select(_id == $fallbackId => 0, 1) asc)[0] {
      ${businessPageFields}
    }
  )
}`;
